/* ============================================================
   PLAN BUILDER — plan-builder.js
   Vanilla ES6 — no framework dependencies
   Requires: window.PB_DATA (injected by plan-builder.liquid)
   ============================================================ */

(function () {
  'use strict';

  /* ── GUARD ──────────────────────────────────────────────── */
  if (!window.PB_DATA) return;

  const DATA          = window.PB_DATA;
  const GRILLS        = DATA.grills                || [];
  const UNIVERSAL_ACC = DATA.universalAccessories  || {};
  const SETTINGS      = DATA.settings              || {};

  // ── API base URL — accessible everywhere in this module ──────────────────
  // Set to your backend URL. Falls back to the section setting, then empty.
  const DEV_API_URL = 'https://squid-app-b288y.ondigitalocean.app'; // ← replace with your actual DO URL

  /* ── METAOBJECT DEBUG ─────────────────────────────────────── */
  // If all tabs are empty → metaobject storefront access is OFF or field keys mismatch.
  // FIX: Shopify Admin → Content → Metaobjects → Universal Accessories
  //      → ⚙ Settings → enable "Storefront access".
  // Expected field keys: gas_accessories_products | beverage_products
  //                      under_counter_products   | essentials_products
  ;(function () {
    const allEmpty = Object.values(UNIVERSAL_ACC).every(a => !Array.isArray(a) || a.length === 0);
    if (allEmpty) {
      console.warn('[PlanBuilder] universalAccessories is EMPTY. ' +
        'Enable storefront access on the metaobject definition in Shopify Admin, ' +
        'or verify field keys match the snippet.');
    } else {
      const counts = Object.entries(UNIVERSAL_ACC).map(([k,v]) => k + ':' + v.length).join(' | ');
      console.log('[PlanBuilder] universalAccessories OK →', counts);
    }
  })();

  const TAB_ORDER = ['gas-accessories', 'beverage', 'under-counter', 'essentials'];
  const TAB_LABELS = {
    'gas-accessories': 'GAS ACCESSORIES',
    'beverage':        'BEVERAGE',
    'under-counter':   'UNDER COUNTER',
    'essentials':      'ESSENTIALS'
  };

  /* ── STATE ──────────────────────────────────────────────── */
  const state = {
    step:       1,
    activeTab:  'gas-accessories',
    planItems:  [],            // [{ itemId, productId, variantId, title, variantTitle, price, image, type, category }]
    modal: {
      open:       false,
      product:    null,        // full product object
      selections: {}           // { 'Size': '42"', 'Burner Configuration': 'Standard' }
    },
    form:             {},
    submission:       { id: null, status: 'idle', pdfUrl: '' },
    variantSelections: {}  // { productId: variantId } — persists user's dropdown choice across re-renders
  };

  /* ── HELPERS ────────────────────────────────────────────── */
  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => [...(ctx || document).querySelectorAll(sel)];
  const el = id => document.getElementById(id);

  function formatPrice(cents) {
    if (cents == null || isNaN(cents)) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', minimumFractionDigits: 2
    }).format(cents / 100);
  }

  function uid() {
    return 'pb_' + Math.random().toString(36).slice(2, 9);
  }

  function sanitize(str) {
    const d = document.createElement('div');
    d.textContent = str || '';
    return d.innerHTML;
  }

  /**
   * Extracts cutout dimensions and specifications from a variant title.
   *
   * Rules:
   *   "Default Title"                              → { cutout: '', specs: '' }
   *   "54 inch / w/ Rotisserie + Sear Zone (RS)"  → { cutout: '54 inch', specs: 'w/ Rotisserie + Sear Zone (RS)' }
   *   "19 inch"                                    → { cutout: '19 inch', specs: '' }
   *   "w/ Rotisserie (R Model)"                    → { cutout: '', specs: 'w/ Rotisserie (R Model)' }
   */
  function extractDimensionsAndSpecs(variantTitle) {
    if (!variantTitle || variantTitle === 'Default Title') {
      return { cutout: '', specs: '' };
    }
    const sizeRe = /^\d+(\.\d+)?\s*(inch|in|"|'|cm|mm)\b/i;
    const parts  = variantTitle.split(/\s*\/\s*/);
    if (parts.length >= 2 && sizeRe.test(parts[0].trim())) {
      return { cutout: parts[0].trim(), specs: parts.slice(1).join(' / ').trim() };
    }
    if (parts.length === 1 && sizeRe.test(parts[0].trim())) {
      return { cutout: parts[0].trim(), specs: '' };
    }
    return { cutout: '', specs: variantTitle.trim() };
  }

  /* ── VARIANT LOGIC ──────────────────────────────────────── */
  function findVariant(product, selections) {
    if (!product || !product.variants) return null;
    if (!product.options || product.options.length === 0) return product.variants[0];

    return product.variants.find(v =>
      product.options.every((opt, i) => {
        const key = 'option' + (i + 1);
        return !selections[opt.name] || v[key] === selections[opt.name];
      })
    ) || product.variants[0];
  }

  function getDefaultSelections(product) {
    if (!product || !product.options) return {};
    const sel = {};
    product.options.forEach((opt, i) => {
      const key = 'option' + (i + 1);
      const avail = product.variants.find(v => v.available !== false);
      sel[opt.name] = avail ? avail[key] : opt.values[0];
    });
    return sel;
  }

  function isVariantAvailable(product, optionName, value, currentSelections) {
    const testSel = Object.assign({}, currentSelections, { [optionName]: value });
    const v = findVariant(product, testSel);
    return v ? v.available !== false : false;
  }

  /**
   * Returns true if ANY variant exists that:
   *   - matches all selections for options 0 … (optIdx-1)
   *   - has option[optIdx] === val
   * Ignores stock/availability — used to decide whether to SHOW a pill at all.
   */
  function variantExistsForValue(product, optIdx, val, selections) {
    const key = 'option' + (optIdx + 1);
    return product.variants.some(function (v) {
      for (let i = 0; i < optIdx; i++) {
        const prevOpt = product.options[i];
        const prevKey = 'option' + (i + 1);
        if (selections[prevOpt.name] && v[prevKey] !== selections[prevOpt.name]) return false;
      }
      return v[key] === val;
    });
  }

  /* ── ACCESSORY FILTERING ────────────────────────────────── */

  /**
   * Returns the grill product object that is currently in the plan.
   */
  function getSelectedGrillProduct() {
    const grillItem = state.planItems.find(i => i.type === 'grill');
    return grillItem ? GRILLS.find(g => g.id === grillItem.productId) : null;
  }

  /**
   * Returns the numeric grill size (e.g. "42") from the selected variant title.
   */
  function getSelectedGrillSize() {
    const grillItem = state.planItems.find(i => i.type === 'grill');
    if (!grillItem) return null;
    const match = String(grillItem.variantTitle || '').match(/(\d+)/);
    return match ? match[1] : null;
  }

  /**
   * Merges universal accessories (from metaobject) and dependent accessories
   * (from the selected grill's product metafields) for the active tab.
   * Deduplicates by product ID so the same product never appears twice.
   *
   * Data sources:
   *   UNIVERSAL_ACC[tab]                                 → always shown
   *   selectedGrill.dependentAccessories[tab]            → shown only when that grill is selected
   */
  function getVisibleAccessories() {
    const universal  = UNIVERSAL_ACC[state.activeTab] || [];
    const grill      = getSelectedGrillProduct();
    const dependent  = grill && grill.dependentAccessories
      ? (grill.dependentAccessories[state.activeTab] || [])
      : [];

    // Dependent (grill-specific / recommended) products first, then universal.
    // Each list's internal order mirrors the Shopify metafield list order,
    // which the client can reorder manually in Shopify Admin.
    const seen = new Set();
    return [...dependent, ...universal].filter(p => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });
  }

  /**
   * For size-variant accessories, attempts to find the variant that matches
   * the customer's selected grill size so it can be pre-selected.
   */
  function getRecommendedVariantForAccessory(acc) {
    const grillSize = getSelectedGrillSize();
    if (!grillSize || !acc.variants || acc.variants.length <= 1) return null;

    return acc.variants.find(v => {
      const nums = [v.option1, v.option2].filter(Boolean).join(' ');
      return nums.match(new RegExp('\\b' + grillSize + '\\b'));
    }) || null;
  }

  /* ── PLAN ITEM MANAGEMENT ───────────────────────────────── */
  function hasInPlan(productId, variantId) {
    return state.planItems.some(i =>
      i.productId === productId && (variantId == null || i.variantId === variantId)
    );
  }

  function addToPlan(item) {
    if (item.type === 'grill') {
      // Replace existing grill
      state.planItems = state.planItems.filter(i => i.type !== 'grill');
    }
    state.planItems.push(item);
    renderSidebar();
    renderNextButton();
  }

  function removeFromPlan(itemId) {
    // Clear stored variant selection for the removed product so badge resets correctly
    const removed = state.planItems.find(i => i.itemId === itemId);
    if (removed && removed.productId) {
      delete state.variantSelections[removed.productId];
    }
    state.planItems = state.planItems.filter(i => i.itemId !== itemId);
    renderSidebar();
    renderNextButton();
    // Refresh acc grid to update "added" button states
    if (state.step === 2) renderAccessoryGrid();
  }

  /* ── STEP NAVIGATION ────────────────────────────────────── */
  function goToStep(n) {
    const from = state.step;
    state.step = n;

    // Hide all steps
    $$('.pb-step').forEach(s => s.classList.add('pb-step--hidden'));

    const stepEl = el('pb-step-' + n);
    if (stepEl) stepEl.classList.remove('pb-step--hidden');

    // Step indicator labels
    const indicators = { 1: 'STEP 1 OF 3', 2: 'STEP 2 OF 3', 3: 'STEP 3 OF 3', 4: 'COMPLETE' };
    const label = indicators[n] || '';

    // Topbar step indicator
    const indEl = el('pb-step-indicator');
    if (indEl) indEl.textContent = label;

    // Back nav — hide entire nav on step 1 and confirmation (step 4)
    const backNav     = el('pb-backnav');
    const backLabel   = el('pb-back-label');
    const backNavStep = el('pb-backnav-step');

    if (backNav) backNav.hidden = (n === 1 || n === 4);
    if (backLabel) {
      const backLabels = { 2: 'BACK TO GRILL SELECTION', 3: 'BACK TO ACCESSORIES' };
      backLabel.textContent = backLabels[n] || '';
    }
    if (backNavStep) backNavStep.textContent = label;

    // Sidebar step label
    const sidebarStepLbl = el('pb-sidebar-step-lbl');
    if (sidebarStepLbl) sidebarStepLbl.textContent = label;

    // Sidebar progress bar — step 1=33%, step 2=66%, step 3/4=100%
    const progressFill = el('pb-progress-fill');
    const progressEl   = el('pb-sidebar-progress');
    const pctMap = { 1: '33.33%', 2: '66.66%', 3: '100%', 4: '100%' };
    if (progressFill) progressFill.style.width = pctMap[n] || '33.33%';
    if (progressEl)   progressEl.setAttribute('aria-valuenow', String(Math.min(n, 3)));

    // Step-specific renders
    if (n === 2 && from === 1) {
      state.activeTab = 'gas-accessories';
      setActiveTab('gas-accessories');
      renderAccessoryGrid();
    }
    if (n === 4) {
      renderConfirmation();
      el('pb-sidebar').classList.add('pb-sidebar--complete');
    }

    renderNextButton();
    // Defer scroll until after DOM updates settle so browser doesn't cancel it
    requestAnimationFrame(function () {
      window.scrollTo({ top: 125, behavior: 'smooth' });
    });
  }

  /* ── NEXT BUTTON ────────────────────────────────────────── */
  function renderNextButton() {
    const btn = el('pb-next-btn');
    const lbl = el('pb-next-label');
    if (!btn || !lbl) return;

    const hasGrill = state.planItems.some(i => i.type === 'grill');

    if (state.step === 1) {
      btn.disabled = !hasGrill;
      btn.setAttribute('aria-disabled', String(!hasGrill));
      lbl.textContent = 'NEXT: ACCESSORIES';
      btn.style.display = '';
    } else if (state.step === 2) {
      btn.disabled = false;
      btn.setAttribute('aria-disabled', 'false');
      const idx = TAB_ORDER.indexOf(state.activeTab);
      const isLast = idx === TAB_ORDER.length - 1;
      lbl.textContent = isLast
        ? 'CONTINUE TO SUBMIT'
        : 'NEXT: ' + TAB_LABELS[TAB_ORDER[idx + 1]];
      btn.style.display = '';
    } else if (state.step === 3) {
      lbl.textContent = 'SEND MY PLAN';
      btn.disabled = false;
      btn.setAttribute('aria-disabled', 'false');
      btn.style.display = '';
    } else {
      btn.style.display = 'none';
    }
  }

  /* ── SIDEBAR ────────────────────────────────────────────── */
  function renderSidebar() {
    const itemsEl  = el('pb-sidebar-items');
    const emptyEl  = el('pb-sidebar-empty');
    const totalEl  = el('pb-total-price');
    const totalRow = el('pb-sidebar-total');

    if (!itemsEl) return;

    const items = state.planItems;
    const count = items.length;

    if (count === 0) {
      if (emptyEl) emptyEl.style.display = '';
      // Remove non-empty items
      $$('.pb-sidebar__item', itemsEl).forEach(n => n.remove());
      if (totalRow) totalRow.style.visibility = 'hidden';
      return;
    }

    if (emptyEl) emptyEl.style.display = 'none';
    if (totalRow) totalRow.style.visibility = 'visible';

    // Rebuild item list
    $$('.pb-sidebar__item', itemsEl).forEach(n => n.remove());
    const hasAccessories = items.some(i => i.type !== 'grill');

    let total = 0;
    items.forEach(item => {
      total += item.price || 0;
      const isLockedGrill = item.type === 'grill' && hasAccessories;
      const div = document.createElement('div');
      div.className = 'pb-sidebar__item';
      div.dataset.itemId = item.itemId;
      div.innerHTML = `
        <div class="pb-sidebar__item-info">
          <div class="pb-sidebar__item-top">
            <p class="pb-sidebar__item-name">${sanitize(item.title)}</p>
            <p class="pb-sidebar__item-price">${formatPrice(item.price)}</p>
          </div>
          ${item.variantTitle ? `<p class="pb-sidebar__item-variant">${sanitize(item.variantTitle)}</p>` : ''}
        </div>
        <button class="pb-sidebar__item-remove"
                ${isLockedGrill ? 'disabled aria-disabled="true" title="Remove all accessories before removing the grill"' : ''}
                aria-label="Remove ${sanitize(item.title)} from plan"
                data-item-id="${sanitize(item.itemId)}">&#215;</button>
      `;
      itemsEl.appendChild(div);
    });

    if (totalEl) totalEl.textContent = formatPrice(total);
  }

  /* ── STEP 1: GRILL GRID ─────────────────────────────────── */
  function renderGrillGrid() {
    const grid = el('pb-grills-grid');
    if (!grid) return;
    grid.innerHTML = '';

    if (GRILLS.length === 0) {
      grid.innerHTML = '<p class="pb-empty-grid">No grills found. Assign the grill collection in theme settings.</p>';
      return;
    }

    GRILLS.forEach(product => {
      const card = document.createElement('div');
      card.className = 'pb-grill-card';
      card.setAttribute('role', 'listitem');
      card.dataset.productId = product.id;

      const badge = '';
      const desc  = product.description || '';
      const img   = product.featuredImage || '';

      card.innerHTML = `
        <div class="pb-grill-card__img-wrap${product.secondImage ? ' pb-has-img2' : ''}"${product.secondImage ? ` style="--pb-img2:url('${product.secondImage}')"` : ''}>
          <img class="pb-grill-card__img"
               src="${sanitize(img)}"
               alt="${sanitize(product.title)}"
               loading="lazy">
        </div>
        <div class="pb-grill-card__body">
          ${badge ? `<p class="pb-grill-card__badge">${sanitize(badge)}</p>` : ''}
          <h3 class="pb-grill-card__name">${sanitize(product.title)}</h3>
          <p class="pb-grill-card__desc">${sanitize(desc)}</p>
          <button class="pb-btn pb-btn--primary pb-grill-select-btn"
                  type="button"
                  data-product-id="${product.id}"
                  aria-label="Select options for ${sanitize(product.title)}">
            SELECT OPTIONS
          </button>
        </div>
      `;
      grid.appendChild(card);
    });
  }

  /* ── MODAL ──────────────────────────────────────────────── */
  function openModal(productId) {
    const product = GRILLS.find(p => p.id === productId);
    if (!product) return;

    state.modal.product    = product;
    state.modal.selections = getDefaultSelections(product);
    state.modal.open       = true;

    renderModal();

    const modalEl = el('pb-modal');
    if (modalEl) {
      modalEl.hidden = false;
      document.body.style.overflow = 'hidden';
      // Focus first focusable element
      setTimeout(() => {
        const first = modalEl.querySelector('button, [tabindex]');
        if (first) first.focus();
      }, 50);
    }
  }

  function closeModal() {
    state.modal.open    = false;
    state.modal.product = null;
    const modalEl = el('pb-modal');
    if (modalEl) modalEl.hidden = true;
    document.body.style.overflow = '';
    // Return focus to trigger button
    const focused = document.querySelector('[data-last-focused]');
    if (focused) { focused.focus(); focused.removeAttribute('data-last-focused'); }
  }

  /* ── ACCESSORY IMAGE POPUP ────────────────────────────────── */
  function openAccPopup(productId) {
    const allAcc  = getVisibleAccessories();
    const product = allAcc.find(p => p.id === productId);
    if (!product) return;

    const popup = el('pb-acc-popup');
    if (!popup) return;

    // Resolve which variant is currently active for this product
    const storedVid = state.variantSelections[productId] || null;
    const variant = (storedVid ? product.variants.find(v => v.id === storedVid) : null)
      || product.variants[0];

    const imgEl   = el('pb-acc-popup-img');
    const titleEl = el('pb-acc-popup-title');
    const skuEl   = el('pb-acc-popup-sku');
    const descEl  = el('pb-acc-popup-desc');
    const priceEl = el('pb-acc-popup-price');
    const addBtn  = el('pb-acc-popup-add');

    if (imgEl)   { imgEl.src = product.featuredImage || ''; imgEl.alt = product.title; }
    if (titleEl) titleEl.textContent = product.title;
    if (skuEl) {
      const sku = variant && variant.sku ? 'SKU: ' + variant.sku : '';
      skuEl.textContent = sku;
      skuEl.hidden = !sku;
    }
    if (descEl)  descEl.textContent  = product.description || '';
    if (priceEl) priceEl.textContent = formatPrice(variant ? variant.price : product.priceMin);

    // Store identifiers for ADD TO PLAN
    popup.dataset.productId = productId;
    popup.dataset.variantId = variant ? String(variant.id) : '';

    // Reflect current in-plan state on ADD button
    if (addBtn) {
      const inPlan = state.planItems.some(i => i.productId === productId);
      if (inPlan) {
        addBtn.textContent = 'ADDED';
        addBtn.classList.add('pb-btn--added');
      } else {
        addBtn.textContent = 'ADD TO PLAN';
        addBtn.classList.remove('pb-btn--added');
      }
    }

    popup.hidden = false;
    document.body.style.overflow = 'hidden';

    setTimeout(() => {
      const closeBtn = el('pb-acc-popup-close');
      if (closeBtn) closeBtn.focus();
    }, 50);
  }

  function closeAccPopup() {
    const popup = el('pb-acc-popup');
    if (popup) popup.hidden = true;
    document.body.style.overflow = '';
  }

  function renderModal() {
    const product = state.modal.product;
    if (!product) return;

    const variant = findVariant(product, state.modal.selections) || product.variants[0];

    // Title
    const badgeEl = el('pb-modal-badge');
    const titleEl = el('pb-modal-title');
    if (badgeEl) badgeEl.textContent = '';
    if (titleEl) titleEl.textContent = product.title || '';

    // SKU — updates dynamically with every selection change
    const skuEl = el('pb-modal-sku');
    if (skuEl) {
      if (variant.sku) {
        skuEl.textContent = 'SKU: ' + variant.sku;
        skuEl.hidden = false;
      } else {
        skuEl.textContent = '';
        skuEl.hidden = true;
      }
    }

    // Image
    const imgEl = el('pb-modal-img');
    if (imgEl) {
      imgEl.style.opacity = '0.6';
      imgEl.src = variant.image || product.featuredImage || '';
      imgEl.alt = product.title + (variant.title ? ' – ' + variant.title : '');
      imgEl.onload = () => { imgEl.style.opacity = '1'; };
    }

    // Price
    const priceEl = el('pb-modal-price');
    if (priceEl) priceEl.textContent = formatPrice(variant.price);

    // Options
    const optsEl = el('pb-modal-options');
    if (!optsEl) return;
    optsEl.innerHTML = '';

    if (!product.options || product.options[0].name === 'Title') {
      // No real options — single variant product
      return;
    }

    product.options.forEach((opt, optIdx) => {
      const group = document.createElement('div');
      group.className = 'pb-modal__option-group';

      const lbl = document.createElement('span');
      lbl.className = 'pb-modal__option-label';
      lbl.textContent = opt.name;
      group.appendChild(lbl);

      const pills = document.createElement('div');
      pills.className = 'pb-modal__pills';
      pills.setAttribute('role', 'group');
      pills.setAttribute('aria-label', opt.name);

      opt.values.forEach(val => {
        // For dependent options (not the primary/first SIZE option):
        // hide pills whose variant combination doesn't exist for the current SIZE selection.
        // The first option (SIZE) is always shown in full so the user can change it freely.
        if (optIdx > 0 && !variantExistsForValue(product, optIdx, val, state.modal.selections)) {
          return; // skip — no variant exists for this combination
        }

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pb-pill';
        btn.textContent = val;
        btn.dataset.option = opt.name;
        btn.dataset.value  = val;
        btn.setAttribute('aria-pressed', String(state.modal.selections[opt.name] === val));
        if (state.modal.selections[opt.name] === val) btn.classList.add('pb-pill--active');

        pills.appendChild(btn);
      });

      group.appendChild(pills);
      optsEl.appendChild(group);
    });
  }

  function updateModalSelection(optionName, value) {
    state.modal.selections[optionName] = value;

    // When a primary option (e.g. SIZE) changes, validate all subsequent options.
    // If the current selection for a later option no longer has a matching variant,
    // auto-pick the first value that does exist for the new combination.
    const product = state.modal.product;
    if (product && product.options) {
      const changedIdx = product.options.findIndex(o => o.name === optionName);
      product.options.forEach((opt, optIdx) => {
        if (optIdx <= changedIdx) return; // only check options that come AFTER the changed one
        const currentVal = state.modal.selections[opt.name];
        if (!currentVal) return;
        const stillExists = variantExistsForValue(product, optIdx, currentVal, state.modal.selections);
        if (!stillExists) {
          // Auto-select the first value that exists for the new parent selection
          const firstValid = opt.values.find(v =>
            variantExistsForValue(product, optIdx, v, state.modal.selections)
          );
          state.modal.selections[opt.name] = firstValid || null;
        }
      });
    }

    renderModal();
  }

  function addGrillToPlan() {
    const product = state.modal.product;
    if (!product) return;

    const variant = findVariant(product, state.modal.selections) || product.variants[0];
    if (!variant) return;

    // Build variant title from selections
    const variantParts = product.options
      ? product.options.map((opt, i) => state.modal.selections[opt.name] || variant['option' + (i + 1)])
      : [];
    const variantTitle = variantParts.filter(Boolean).join(' / ');

    const grillDims = extractDimensionsAndSpecs(variantTitle);

    /* ── Spec file resolution — variant → product fallback ──────────────
       1. Use the selected variant's Product Specification metafield.
       2. If blank/empty, fall back to the product-level spec PDF.
       3. If neither exists → hasPdf: false (listed in summary, no spec page). */
    const resolvedSpecPdfUrl = (variant.specPdfUrl && variant.specPdfUrl.trim())
      ? variant.specPdfUrl
      : (product.productSpecPdfUrl && product.productSpecPdfUrl.trim())
        ? product.productSpecPdfUrl
        : '';

    console.log(
      '[PlanBuilder] Adding grill — variantId:', variant.id,
      '\n  variant spec:', variant.specPdfUrl || '(empty)',
      '\n  product spec (fallback):', product.productSpecPdfUrl || '(empty)',
      '\n  resolved →', resolvedSpecPdfUrl || '(none — list only)',
    );

    addToPlan({
      itemId:           uid(),
      productId:        product.id,
      productHandle:    product.handle || '',
      variantId:        variant.id,
      title:            product.title,
      variantTitle:     variantTitle,
      price:            variant.price,
      image:            variant.image || product.featuredImage || '',
      type:             'grill',
      category:         null,
      sku:              variant.sku || '',
      cutoutDimensions: grillDims.cutout,
      specifications:   grillDims.specs,
      specPdfUrl:       resolvedSpecPdfUrl,
    });

    closeModal();
    renderGrillGrid(); // refresh to show any "in plan" state
  }

  /* ── STEP 2: ACCESSORY GRID ─────────────────────────────── */
  function setActiveTab(tab) {
    state.activeTab = tab;
    $$('.pb-tab').forEach(btn => {
      const isActive = btn.dataset.tab === tab;
      btn.classList.toggle('pb-tab--active', isActive);
      btn.setAttribute('aria-selected', String(isActive));
    });
    renderAccessoryGrid();
    renderNextButton();
  }

  function renderAccessoryGrid() {
    const grid = el('pb-accessories-grid');
    if (!grid) return;
    grid.innerHTML = '';

    const visible = getVisibleAccessories();

    if (visible.length === 0) {
      grid.innerHTML = '<p class="pb-empty-grid">No accessories found for this selection.</p>';
      return;
    }

    // Recommended auto-select only applies to dependent tabs (Essentials + Under Counter)
    const isDependent = state.activeTab === 'under-counter' || state.activeTab === 'essentials';

    visible.forEach(product => {
      const recommended = isDependent ? getRecommendedVariantForAccessory(product) : null;
      const inPlan = state.planItems.some(i => i.productId === product.id);

      const card = document.createElement('div');
      card.className = 'pb-acc-card';
      card.setAttribute('role', 'listitem');

      const img = product.featuredImage || '';
      const hasMultiVariant = product.options &&
        product.options.length > 0 &&
        product.options[0].name !== 'Title' &&
        product.variants.length > 1;

      // Build variant selector if applicable
      // ESSENTIALS: always pre-select (recommended if available, else first variant); button always enabled
      // UNDER COUNTER: if recommended → pre-select + enable; if no match → placeholder + disabled
      let variantSelector = '';
      const isEssentials   = state.activeTab === 'essentials';
      const isUnderCounter = state.activeTab === 'under-counter';
      // Show "Select Size" placeholder only for under-counter WITH real size variants when nothing is recommended.
      // Single-variant ("Default Title") under-counter products need no selection → no placeholder, button enabled.
      const showPlaceholder = isUnderCounter && !recommended && hasMultiVariant;

      // Restore user's previous dropdown choice (survives re-renders)
      const storedVariantId = state.variantSelections[product.id] || null;

      if (hasMultiVariant) {
        const opts = product.variants.map((v, idx) => {
          let sel = '';
          if (storedVariantId) {
            // Always honour what the user explicitly chose
            if (v.id === storedVariantId) sel = ' selected';
          } else if (recommended && recommended.id === v.id) {
            sel = ' selected'; // recommended pre-selects when no stored choice
          } else if (!recommended && !showPlaceholder && idx === 0) {
            sel = ' selected'; // essentials fallback: first variant
          }
          return `<option value="${v.id}" data-price="${v.price}" data-sku="${sanitize(v.sku || '')}"${sel}>${sanitize(v.title)}</option>`;
        }).join('');
        const placeholderOpt = showPlaceholder
          ? `<option value="" disabled selected>Select Size</option>`
          : '';
        variantSelector = `<select class="pb-acc-card__variant-select pb-acc-variant-select"
          data-product-id="${product.id}" aria-label="Select size for ${sanitize(product.title)}">
          ${placeholderOpt}${opts}
        </select>`;
      }

      const grillSize = getSelectedGrillSize();
      // Badge always rendered in DOM (so JS can toggle it), hidden via style when not applicable.
      // Hidden when user has an explicit stored choice that differs from recommended.
      const badgeHidden = storedVariantId && recommended && storedVariantId !== recommended.id;
      const recommendLabel = recommended
        ? `<span class="pb-acc-card__recommend-tag"${badgeHidden ? ' style="display:none"' : ''}>&#9733; RECOMMENDED${grillSize ? ' FOR ' + grillSize + '"' : ''}</span>`
        : '';

      // Resolve which variant is currently "active" for SKU display
      let activeVariant = recommended || product.variants[0];
      if (storedVariantId) {
        const stored = product.variants.find(v => v.id === storedVariantId);
        if (stored) activeVariant = stored;
      }
      const defaultVariant = activeVariant;
      const displayPrice = hasMultiVariant
        ? (product.priceMin === product.priceMax
            ? formatPrice(product.priceMin)
            : 'From ' + formatPrice(product.priceMin))
        : formatPrice(defaultVariant ? defaultVariant.price : product.priceMin);

      // SKU: show the active variant's SKU; for multi-variant this updates on dropdown change
      const initialSku   = defaultVariant && defaultVariant.sku ? defaultVariant.sku : '';
      const skuHtml      = `<p class="pb-acc-card__sku"${initialSku ? '' : ' hidden'}>${initialSku ? 'SKU: ' + sanitize(initialSku) : ''}</p>`;

      const btnClass    = inPlan ? 'pb-btn pb-btn--primary pb-btn--add pb-btn--added' : 'pb-btn pb-btn--primary pb-btn--add';
      const btnText     = inPlan ? 'ADDED' : 'ADD TO PLAN';
      // Disable button only when showing placeholder (under-counter, no recommendation)
      const btnDisabled = (!inPlan && showPlaceholder) ? ' disabled' : '';

      card.innerHTML = `
        <div class="pb-acc-card__img-wrap pb-acc-img-trigger"
             role="button" tabindex="0"
             data-product-id="${product.id}"
             aria-label="View ${sanitize(product.title)} details">
          <img class="pb-acc-card__img"
               src="${sanitize(img)}"
               alt="${sanitize(product.title)}"
               loading="lazy">
        </div>
        <div class="pb-acc-card__body">
          <h3 class="pb-acc-card__name">${sanitize(product.title)}</h3>
          ${skuHtml}
          <p class="pb-acc-card__desc">${sanitize(product.description || '')}</p>
          ${recommendLabel}
          <p class="pb-acc-card__price pb-acc-price" data-product-id="${product.id}">${displayPrice}</p>
          ${variantSelector}
          <button class="${btnClass} pb-acc-add-btn"
                  type="button"
                  data-product-id="${product.id}"
                  aria-label="${inPlan ? 'Remove' : 'Add'} ${sanitize(product.title)} ${inPlan ? 'from' : 'to'} plan"
                  aria-pressed="${String(inPlan)}"${btnDisabled}>
            ${btnText}
          </button>
        </div>
      `;
      grid.appendChild(card);
    });

    // Show scroll hint if grid overflows the viewport
    initScrollHint(grid);
  }

  /* ── SCROLL HINT ─────────────────────────────────────────────── */
  function initScrollHint(grid) {
    const hint = el('pb-scroll-hint');
    if (!hint || !grid) return;

    // Detach previous scroll listener on tab change
    if (hint._scrollHandler) {
      window.removeEventListener('scroll', hint._scrollHandler);
      hint._scrollHandler = null;
    }

    function checkOverflow() {
      const rect = grid.getBoundingClientRect();
      const hasOverflow = rect.bottom > window.innerHeight + 40;
      hint.hidden = !hasOverflow;
    }

    hint._scrollHandler = checkOverflow;
    window.addEventListener('scroll', checkOverflow, { passive: true });

    // Measure after grid has painted
    setTimeout(checkOverflow, 80);

    // Click: smooth-scroll grid bottom into view
    hint.onclick = function () {
      grid.scrollIntoView({ behavior: 'smooth', block: 'end' });
    };
  }

  function addAccessoryToPlan(productId, variantId) {
    // Look up product from the currently visible list (universal + dependent)
    const product = getVisibleAccessories().find(p => p.id === productId);
    if (!product) return;

    // Toggle: if already in plan remove it
    const existing = state.planItems.find(i => i.productId === productId);
    if (existing) {
      removeFromPlan(existing.itemId);
      return;
    }

    const variant = product.variants.find(v => v.id === variantId) || product.variants[0];
    if (!variant) return;

    const cat = state.activeTab;

    const vTitle   = variant.title || 'Default Title';
    const accDims  = extractDimensionsAndSpecs(vTitle);

    /* ── Spec file resolution — variant → product fallback ──────────────
       1. Use the selected variant's Product Specification metafield.
       2. If blank/empty, fall back to the product-level spec PDF.
       3. If neither exists → hasPdf: false (listed in summary, no spec page). */
    const resolvedAccSpecUrl = (variant.specPdfUrl && variant.specPdfUrl.trim())
      ? variant.specPdfUrl
      : (product.productSpecPdfUrl && product.productSpecPdfUrl.trim())
        ? product.productSpecPdfUrl
        : '';

    console.log(
      '[PlanBuilder] Adding accessory — variantId:', variant.id,
      '\n  variant spec:', variant.specPdfUrl || '(empty)',
      '\n  product spec (fallback):', product.productSpecPdfUrl || '(empty)',
      '\n  resolved →', resolvedAccSpecUrl || '(none — list only)',
    );

    addToPlan({
      itemId:           uid(),
      productId:        product.id,
      productHandle:    product.handle || '',
      variantId:        variant.id,
      title:            product.title,
      variantTitle:     vTitle,
      price:            variant.price,
      image:            product.featuredImage || '',
      type:             'accessory',
      category:         cat,
      sku:              variant.sku || '',
      cutoutDimensions: accDims.cutout,
      specifications:   accDims.specs,
      specPdfUrl:       resolvedAccSpecUrl,
    });

    // Refresh grid to update button state
    renderAccessoryGrid();
  }

  /* ── STEP 3: FORM ───────────────────────────────────────── */
  function validateForm() {
    const fields = [
      { id: 'pb-first-name', errId: 'pb-first-name-err', msg: 'First name is required.' },
      { id: 'pb-last-name',  errId: 'pb-last-name-err',  msg: 'Last name is required.' },
      { id: 'pb-email',      errId: 'pb-email-err',       msg: 'A valid email address is required.' },
      { id: 'pb-phone',      errId: 'pb-phone-err',        msg: 'Phone number is required.' },
      { id: 'pb-zip',        errId: 'pb-zip-err',          msg: 'ZIP code is required.' }
    ];

    let valid = true;

    fields.forEach(f => {
      const input = el(f.id);
      const errEl = el(f.errId);
      if (!input) return;
      let msg = '';

      if (!input.value.trim()) {
        msg = f.msg;
      } else if (f.id === 'pb-email') {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!re.test(input.value.trim())) msg = f.msg;
      }

      if (errEl) errEl.textContent = msg;
      input.classList.toggle('pb-form__input--error', !!msg);
      if (msg) valid = false;
    });

    // Consent
    const consent = el('pb-consent');
    const consentErr = el('pb-consent-err');
    if (consent && !consent.checked) {
      if (consentErr) consentErr.textContent = 'Please agree to continue.';
      valid = false;
    } else if (consentErr) {
      consentErr.textContent = '';
    }

    return valid;
  }

  function collectFormData() {
    return {
      firstName: (el('pb-first-name') || {}).value || '',
      lastName:  (el('pb-last-name')  || {}).value || '',
      email:     (el('pb-email')      || {}).value || '',
      phone:     (el('pb-phone')      || {}).value || '',
      zip:       (el('pb-zip')        || {}).value || '',
      timeline:  (el('pb-timeline')   || {}).value || '',
      notes:     (el('pb-notes')      || {}).value || ''
    };
  }

  function submitPlan() {
    if (!validateForm()) return;

    const submitBtn  = el('pb-form-submit');
    const globalErr  = el('pb-form-error');
    const sidebarBtn = el('pb-next-btn');

    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.classList.add('pb-btn--loading');
      submitBtn.textContent = 'SENDING...';
    }
    if (globalErr) globalErr.hidden = true;
    if (sidebarBtn) sidebarBtn.disabled = true;

    /* Lock the entire page — transparent overlay catches all pointer events */
    const overlay = el('pb-submit-overlay');
    if (overlay) overlay.hidden = false;

    state.form = collectFormData();
    state.submission.status = 'loading';

    /* ── Build payload matching backend schema ── */
    const grillItem = state.planItems.find(i => i.type === 'grill');
    const accItems  = state.planItems.filter(i => i.type === 'accessory');
    const totalMsrp = state.planItems.reduce((sum, i) => sum + (i.price || 0), 0);

    /* ── Spec file type detection ────────────────────────────────────────
       The Product Specification metafield accepts any Shopify file (PDF or
       image). We detect the type from the URL extension so the backend can
       handle each correctly:
         specFileType: 'pdf'   → merge PDF pages directly into main PDF
         specFileType: 'image' → embed image as a full-size PDF page
         specFileType: null    → no spec file; list-only in summary table  */
    function getSpecFileType(url) {
      if (!url || !url.trim()) return null;
      const clean = url.split('?')[0].toLowerCase();
      if (/\.(jpg|jpeg|png|gif|webp|bmp|tiff|svg)(\b|$)/.test(clean)) return 'image';
      if (/\.pdf(\b|$)/.test(clean)) return 'pdf';
      /* Shopify CDN URLs sometimes omit extension — fall back to 'pdf' */
      return 'pdf';
    }

    function hasSpecFile(item) {
      /* Returns true if the item has ANY spec file (PDF or image) */
      return !!(item.specPdfUrl && item.specPdfUrl.trim() !== '');
    }

    /* ── DEBUG: log spec file status for every plan item ── */
    console.group('[PlanBuilder] Spec files at submission');
    if (grillItem) {
      const ft = getSpecFileType(grillItem.specPdfUrl);
      console.log(
        '%c GRILL %c ' + grillItem.title + ' / ' + (grillItem.variantTitle || 'Default')
        + '  ' + (ft ? ('✓ ' + ft) : '✗ no spec file — list only'),
        'background:#0d0d0d;color:#fff;padding:1px 4px', 'font-weight:bold',
      );
      console.log('  specFileUrl:', grillItem.specPdfUrl || '(empty)');
    }
    accItems.forEach(function (a, i) {
      const ft = getSpecFileType(a.specPdfUrl);
      const tag = ft ? ('✓ ' + ft + ' spec') : '✗ no spec file — list only';
      console.log(
        '%c ACC ' + (i + 1) + ' %c ' + a.title + '  ' + tag,
        ft ? 'background:#4b5050;color:#fff;padding:1px 4px' : 'background:#cc7700;color:#fff;padding:1px 4px',
        'font-weight:bold',
      );
      if (a.specPdfUrl) console.log('  specFileUrl:', a.specPdfUrl, '| type:', ft);
    });
    console.log('brandLogoUrl:', DATA.brandLogoUrl || '(none)');
    console.groupEnd();

    const payload = {
      brand:        DATA.brand        || 'twin-eagles',
      brandLogoUrl: DATA.brandLogoUrl || '',

      customer: {
        firstName: state.form.firstName || '',
        lastName:  state.form.lastName  || '',
        email:     state.form.email     || '',
        phone:     state.form.phone     || '',
        zip:       state.form.zip       || '',
        timeline:  state.form.timeline  || '',
        notes:     state.form.notes     || '',
      },

      plan: {
        /* Grill — always sent so it appears in the summary table.
           specFileType tells the backend how to embed the spec file:
             'pdf'   → merge PDF pages into main PDF
             'image' → embed image as a PDF page
             null    → no spec file; show in summary list only */
        grill: grillItem ? {
          productId:        grillItem.productId,
          productHandle:    grillItem.productHandle    || '',
          title:            grillItem.title,
          variantId:        grillItem.variantId    || null,
          variantTitle:     grillItem.variantTitle || '',
          sku:              grillItem.sku          || '',
          price:            grillItem.price        || 0,
          image:            grillItem.image        || '',
          cutoutDimensions: grillItem.cutoutDimensions || '',
          specifications:   grillItem.specifications   || '',
          specFileUrl:      grillItem.specPdfUrl       || '',   /* renamed: supports PDF + image */
          specPdfUrl:       grillItem.specPdfUrl       || '',   /* kept for backward compat */
          specFileType:     getSpecFileType(grillItem.specPdfUrl),
          hasPdf:           hasSpecFile(grillItem),
        } : null,

        /* ALL accessories in one array — this is what the backend iterates for
           the "Selected Products" summary table, so every item the customer
           selected will appear in the PDF list regardless of spec file status.

           Backend logic per item:
             hasPdf: true  + specFileType: 'pdf'   → merge spec PDF pages + list in summary
             hasPdf: true  + specFileType: 'image' → embed image as PDF page + list in summary
             hasPdf: false + specFileType: null    → list in summary ONLY, no spec page */
        accessories: accItems.map(function (a) {
          var ft = getSpecFileType(a.specPdfUrl);
          return {
            productId:        a.productId,
            productHandle:    a.productHandle    || '',
            title:            a.title,
            variantId:        a.variantId    || null,
            variantTitle:     a.variantTitle || '',
            sku:              a.sku          || '',
            category:         a.category    || 'other',
            price:            a.price       || 0,
            image:            a.image       || '',
            cutoutDimensions: a.cutoutDimensions || '',
            specifications:   a.specifications   || '',
            specFileUrl:      a.specPdfUrl       || '',   /* renamed: supports PDF + image */
            specPdfUrl:       a.specPdfUrl       || '',   /* kept for backward compat */
            specFileType:     ft,
            hasPdf:           hasSpecFile(a),
          };
        }),

        /* accessoriesListOnly: kept as empty array for backward compat.
           All items are now in accessories[] with hasPdf flag. */
        accessoriesListOnly: [],

        totalMsrp,
      },

      // Honeypot — must stay empty; bots fill it
      _hp: '',
    };

    const apiBase = DEV_API_URL || DATA.planApiUrl || '';
    const apiKey  = DATA.planApiKey || 'f5add03141e83cb2801f6fd68e024af5cb7169fdb18c3ba98b25b7f72ebba196';

    if (!apiBase) {
      console.error('[PlanBuilder] No API URL set. Add plan_api_url in section settings or set DEV_API_URL in plan-builder.js');
      if (globalErr) {
        globalErr.textContent = 'Configuration error: API URL not set. Contact the site administrator.';
        globalErr.hidden = false;
      }
      if (submitBtn) { submitBtn.disabled = false; submitBtn.classList.remove('pb-btn--loading'); submitBtn.textContent = 'SEND MY PLAN'; }
      if (sidebarBtn) sidebarBtn.disabled = false;
      if (overlay) overlay.hidden = true;
      return;
    }

    fetch(`${apiBase}/api/plans`, {
      method:  'POST', 
      headers: {
        'Content-Type':            'application/json',
        'X-Plan-API-Key':          apiKey,
        'bypass-tunnel-reminder':  '1',   // bypasses localtunnel splash page
      },
      body: JSON.stringify(payload),
    })
      .then(res => res.json().then(data => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data.error || `Server error ${ok}`);
        state.submission.id     = data.planId || ('TE-' + Date.now());
        state.submission.pdfUrl = data.pdfUrl || data.downloadUrl || '';
        state.submission.status = 'success';
        /* Release the page lock — submission complete */
        if (overlay) overlay.hidden = true;
        goToStep(4); 
      })
      .catch(err => {
        console.error('[PlanBuilder] Submit error:', err);
        state.submission.status = 'error';
        if (globalErr) {
          globalErr.textContent = err.message.includes('Too many')
            ? 'Too many submissions. Please wait a few minutes and try again.'
            : 'Something went wrong. Please try again or contact us directly.';
          globalErr.hidden = false;
        }
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.classList.remove('pb-btn--loading');
          submitBtn.textContent = 'SEND MY PLAN';
        }
        if (sidebarBtn) sidebarBtn.disabled = false;
        if (overlay) overlay.hidden = true;
      });
  }

  /* ── PDF DOWNLOAD ───────────────────────────────────────── */
  /**
   * Fetches the plan PDF with the API key header (avoids the 403 Forbidden
   * that a bare browser GET receives), converts it to a Blob, and triggers
   * a native file-save dialog via a temporary object URL.
   */
  function downloadPlanPdf() {
    const planId = state.submission.id;
    const pdfUrl = state.submission.pdfUrl
      || (planId ? (DEV_API_URL + '/api/plans/' + planId + '/pdf/public') : '');

    if (!pdfUrl) {
      console.warn('[PlanBuilder] downloadPlanPdf: no PDF URL available');
      return;
    }

    const btn = el('pb-confirm-pdf-btn');
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'PREPARING PDF…';
    }

    const apiKey = DATA.planApiKey || 'f5add03141e83cb2801f6fd68e024af5cb7169fdb18c3ba98b25b7f72ebba196';

    fetch(pdfUrl, {
      method: 'GET',
      headers: {
        'X-Plan-API-Key':         apiKey,
        'bypass-tunnel-reminder': '1',
      }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Server responded ' + res.status);
        return res.blob();
      })
      .then(function (blob) {
        // Force a file-save dialog regardless of Content-Disposition header
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href     = objectUrl;
        a.download = 'outdoor-kitchen-plan-' + (planId || 'download') + '.pdf';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(function () { URL.revokeObjectURL(objectUrl); }, 10000);

        if (btn) {
          btn.disabled = false;
          btn.textContent = 'DOWNLOAD YOUR PLAN PDF';
        }
      })
      .catch(function (err) {
        console.error('[PlanBuilder] PDF download error:', err);
        if (btn) {
          btn.disabled = false;
          btn.textContent = 'DOWNLOAD FAILED — TRY AGAIN';
          btn.style.background = '#cc0000';
        }
      });
  }

  /* ── STEP 4: CONFIRMATION ───────────────────────────────── */
  function renderConfirmation() {
    const refEl     = el('pb-ref-id');
    const summaryEl = el('pb-confirm-summary');

    if (refEl) refEl.textContent = state.submission.id || '—';

    // PDF download button — JS fetch + blob so the API key header is included
    const pdfRow = el('pb-confirm-pdf-row');
    if (pdfRow) {
      const hasPdf = !!(state.submission.pdfUrl || state.submission.id);
      if (hasPdf) {
        pdfRow.innerHTML = `
          <button class="pb-confirm__pdf-link" id="pb-confirm-pdf-btn" type="button"
                  aria-label="Download your outdoor kitchen plan as PDF">
            DOWNLOAD YOUR PLAN PDF
          </button>`;
        pdfRow.hidden = false;
      } else {
        pdfRow.hidden = true;
      }
    }

    if (!summaryEl) return;
    summaryEl.innerHTML = '';

    let total = 0;
    state.planItems.forEach(item => {
      total += item.price || 0;
      const row = document.createElement('div');
      row.className = 'pb-confirm__summary-item';
      row.innerHTML = `
        <span class="pb-confirm__summary-name">${sanitize(item.title)}
          ${item.variantTitle ? '<small style="font-weight:400;text-transform:none;letter-spacing:0"> — ' + sanitize(item.variantTitle) + '</small>' : ''}
        </span>
        <span class="pb-confirm__summary-price">${formatPrice(item.price)}</span>
      `;
      summaryEl.appendChild(row);
    });

    // Total row
    const totalRow = document.createElement('div');
    totalRow.className = 'pb-confirm__summary-item';
    totalRow.style.fontWeight = '700';
    totalRow.innerHTML = `
      <span class="pb-confirm__summary-name" style="color:#0a0a0a">TOTAL MSRP</span>
      <span class="pb-confirm__summary-price" style="font-size:15px">${formatPrice(total)}</span>
    `;
    summaryEl.appendChild(totalRow);

    // Update sidebar next button
    const sidebarBtn = el('pb-next-btn');
    const sidebarLbl = el('pb-next-label');
    if (sidebarBtn && sidebarLbl) {
      sidebarLbl.textContent = 'PLAN SENT';
      sidebarBtn.disabled = true;
      sidebarBtn.style.background = '#2d7a2d';
      sidebarBtn.style.color = '#fff';
    }
  }

  function resetPlan() {
    state.step       = 1;
    state.activeTab  = 'gas-accessories';
    state.planItems  = [];
    state.modal      = { open: false, product: null, selections: {} };
    state.form             = {};
    state.submission       = { id: null, status: 'idle', pdfUrl: '' };
    state.variantSelections = {};

    // Reset form fields
    const form = el('pb-form');
    if (form) form.reset();

    // Reset sidebar
    el('pb-sidebar').classList.remove('pb-sidebar--complete');
    const sidebarBtn = el('pb-next-btn');
    if (sidebarBtn) {
      sidebarBtn.style.background = '';
      sidebarBtn.style.color = '';
    }

    renderGrillGrid();
    renderSidebar();
    goToStep(1);
  }

  /* ── EVENT DELEGATION ───────────────────────────────────── */
  function bindEvents() {
    const app = el('pb-app');
    if (!app) return;

    // Delegate all clicks within the app
    app.addEventListener('click', function (e) {
      const t = e.target;

      /* — Accessory image (img-wrap only) → acc popup — */
      const accImgTrigger = t.closest('.pb-acc-img-trigger');
      if (accImgTrigger) {
        openAccPopup(Number(accImgTrigger.dataset.productId));
        return;
      }

      /* — Grill card (entire card) → variant modal — */
      const grillCard = t.closest('.pb-grill-card');
      if (grillCard) {
        grillCard.setAttribute('data-last-focused', '');
        openModal(Number(grillCard.dataset.productId));
        return;
      }

      /* — Modal pill selection — */
      const pill = t.closest('.pb-pill');
      if (pill && !pill.classList.contains('pb-pill--unavailable')) {
        updateModalSelection(pill.dataset.option, pill.dataset.value);
        return;
      }

      /* — Modal ADD TO PLAN — */
      if (t.closest('#pb-modal-add')) {
        addGrillToPlan();
        return;
      }

      /* — Modal close button — */
      if (t.closest('#pb-modal-close')) { closeModal(); return; }

      /* — Modal backdrop click — */
      if (t.id === 'pb-modal-backdrop') { closeModal(); return; }

      /* — Acc popup: close button or backdrop — */
      if (t.closest('#pb-acc-popup-close') || t.id === 'pb-acc-popup-backdrop') {
        closeAccPopup(); return;
      }

      /* — Acc popup: ADD TO PLAN — popup stays open; button switches to ADDED ✓ */
      if (t.closest('#pb-acc-popup-add')) {
        const popup  = el('pb-acc-popup');
        const pid    = Number(popup && popup.dataset.productId);
        const vid    = Number(popup && popup.dataset.variantId) || null;
        const addBtn = el('pb-acc-popup-add');
        if (pid) {
          const inPlan = state.planItems.some(i => i.productId === pid);
          if (!inPlan) {
            addAccessoryToPlan(pid, vid);
            if (addBtn) {
              addBtn.textContent = 'ADDED';
              addBtn.classList.add('pb-btn--added');
            }
          }
        }
        return;
      }

      /* — Accessory ADD TO PLAN (card button) — */
      const accBtn = t.closest('.pb-acc-add-btn');
      if (accBtn) {
        const productId = Number(accBtn.dataset.productId);
        const card      = accBtn.closest('.pb-acc-card');
        const varSelect = card ? card.querySelector('.pb-acc-variant-select') : null;
        const variantId = varSelect ? Number(varSelect.value) : null;
        addAccessoryToPlan(productId, variantId);
        return;
      }

      /* — Tab switch — */
      const tab = t.closest('.pb-tab');
      if (tab) { setActiveTab(tab.dataset.tab); return; }

      /* — Sidebar remove — */
      const removeBtn = t.closest('.pb-sidebar__item-remove');
      if (removeBtn) { removeFromPlan(removeBtn.dataset.itemId); return; }

      /* — Back button — */
      if (t.closest('#pb-back-btn')) {
        goToStep(state.step - 1);
        return;
      }

      /* — Next button — */
      if (t.closest('#pb-next-btn')) {
        if (state.step === 1) {
          goToStep(2);
        } else if (state.step === 2) {
          const idx = TAB_ORDER.indexOf(state.activeTab);
          if (idx < TAB_ORDER.length - 1) {
            setActiveTab(TAB_ORDER[idx + 1]);
            // Scroll left panel back to top when advancing between accessory tabs
            requestAnimationFrame(function () {
              window.scrollTo({ top: 125, behavior: 'smooth' });
            });
          } else {
            goToStep(3);
          }
        } else if (state.step === 3) {
          // sidebar "SEND MY PLAN" = trigger form submit
          const form = el('pb-form');
          if (form) form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
        return;
      }

      /* — PDF download — */
      if (t.closest('#pb-confirm-pdf-btn')) { downloadPlanPdf(); return; }

      /* — Rebuild plan — */
      if (t.closest('#pb-rebuild-btn')) { resetPlan(); return; }
    });

    /* — Variant select: update price label + enable ADD TO PLAN button — */
    app.addEventListener('change', function (e) {
      const sel = e.target.closest('.pb-acc-variant-select');
      if (!sel) return;
      const productId   = Number(sel.dataset.productId);
      const selectedOpt = sel.options[sel.selectedIndex];
      const price       = selectedOpt ? Number(selectedOpt.dataset.price) : 0;

      // Update displayed price
      const priceEl = app.querySelector(`.pb-acc-price[data-product-id="${productId}"]`);
      if (priceEl && price) priceEl.textContent = formatPrice(price);

      // Update displayed SKU
      const skuVal  = selectedOpt ? (selectedOpt.dataset.sku || '') : '';
      const skuEl   = sel.closest('.pb-acc-card') && sel.closest('.pb-acc-card').querySelector('.pb-acc-card__sku');
      if (skuEl) {
        if (skuVal) {
          skuEl.textContent = 'SKU: ' + skuVal;
          skuEl.hidden = false;
        } else {
          skuEl.textContent = '';
          skuEl.hidden = true;
        }
      }

      // Persist the user's choice so re-renders don't reset the dropdown
      if (sel.value) {
        state.variantSelections[productId] = Number(sel.value);
      }

      // Enable the ADD TO PLAN button once a real size is chosen
      const card    = sel.closest('.pb-acc-card');
      const addBtn  = card ? card.querySelector('.pb-acc-add-btn') : null;
      if (addBtn && sel.value) {
        addBtn.disabled = false;
      }

      // Per-card recommend badge: hide if user selects a non-recommended variant,
      // restore if they switch back to the recommended one.
      const isDependent = state.activeTab === 'under-counter' || state.activeTab === 'essentials';
      if (isDependent && card) {
        const badge = card.querySelector('.pb-acc-card__recommend-tag');
        if (badge) {
          const product = getVisibleAccessories().find(function(p) { return p.id === productId; });
          if (product) {
            const recommended = getRecommendedVariantForAccessory(product);
            if (recommended) {
              badge.style.display = (Number(sel.value) === recommended.id) ? '' : 'none';
            }
          }
        }
      }
    });

    /* — Form submit — */
    const form = el('pb-form');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        submitPlan();
      });
    }

    /* — ESC closes modal — */
    // Document-level handler catches ESC regardless of focus position.
    // stopPropagation prevents the Impulse theme from consuming the event first.
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        // Close whichever popup is open (acc popup takes priority as it sits above variant modal)
        const accPopup = el('pb-acc-popup');
        if (accPopup && !accPopup.hidden) { closeAccPopup(); return; }
        if (state.modal.open) closeModal();
      }
    }, true); // capture phase — fires before any theme handlers

    // Second handler directly on the modal element as a belt-and-suspenders fallback.
    const modalEscEl = el('pb-modal');
    if (modalEscEl) {
      modalEscEl.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          closeModal();
        }
      });
    }

    /* — Keyboard activation for accessory image trigger (role=button) — */
    app.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        const accImgTrigger = e.target.closest('.pb-acc-img-trigger');
        if (accImgTrigger) {
          e.preventDefault();
          openAccPopup(Number(accImgTrigger.dataset.productId));
        }
      }
    });

    /* — Focus trap in modal — */
    const modal = el('pb-modal');
    if (modal) {
      modal.addEventListener('keydown', function (e) {
        if (e.key !== 'Tab') return;
        const focusable = [...modal.querySelectorAll(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
        )];
        if (!focusable.length) return;
        const first = focusable[0];
        const last  = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault(); first.focus();
        }
      });
    }

    /* — Live input validation clearing — */
    ['pb-first-name','pb-last-name','pb-email','pb-phone','pb-zip'].forEach(id => {
      const input = el(id);
      const errId = id + '-err';
      if (input) {
        input.addEventListener('input', function () {
          input.classList.remove('pb-form__input--error');
          const errEl = el(errId);
          if (errEl) errEl.textContent = '';
        });
      }
    });
  }

  /* ── STICKY NEXT — OLD IMPLEMENTATION COMMENTED OUT ────────
  function initStickyNext() { ... } — removed, replaced below
  ────────────────────────────────────────────────────────── */

  /* ── STICKY NEXT — BODY CLASS TOGGLE ───────────────────────
     Adds class "make_nextbtn_sticky" to <body> when the user
     scrolls down to the level of the YOUR PLAN sidebar heading.
     Trigger point: top edge of .pb-layout reaches the bottom of
     the plan-builder topbar (56px from viewport top).
     Removes the class when scrolled back above that point.     */
  function initStickyClass() {
    var layout = document.querySelector('.pb-layout');
    if (!layout) return;

    var TOPBAR_H = 46; // matches --pb-topbar-h

    function onScroll() {
      // Desktop only — never apply on mobile/tablet (≤ 768px)
      if (window.innerWidth <= 768) {
        document.body.classList.remove('make_nextbtn_sticky');
        return;
      }
      var rect = layout.getBoundingClientRect();
      // rect.top > TOPBAR_H → layout below topbar = near top of page → ADD class
      // rect.top <= TOPBAR_H → scrolled down past trigger line → REMOVE class
      if (rect.top > TOPBAR_H) {
        document.body.classList.add('make_nextbtn_sticky');
      } else {
        document.body.classList.remove('make_nextbtn_sticky');
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // evaluate once on load
  }

  /* ── INIT ───────────────────────────────────────────────── */
  function init() {
    renderGrillGrid();
    renderSidebar();
    renderNextButton();
    goToStep(1);
    bindEvents();
    initStickyClass();
    // On initial page load / reload, always start at the very top (position 0).
    // Double rAF ensures this fires after goToStep's own rAF scroll (125) settles.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        window.scrollTo({ top: 0, behavior: 'instant' });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
